import React, { useState } from 'react';
import type { FAQData } from 'types/websiteBuilder';
import styles from './FAQSection.module.scss';

interface FAQSectionEditorProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function getData(data: Record<string, unknown>): FAQData {
  const items = Array.isArray(data.items)
    ? (data.items as Record<string, unknown>[]).map((i) => ({
        question: typeof i.question === 'string' ? i.question : '',
        answer: typeof i.answer === 'string' ? i.answer : '',
      }))
    : [];
  return {
    title: typeof data.title === 'string' ? data.title : 'FAQ',
    items: items.length > 0 ? items : [{ question: '', answer: '' }],
  };
}

export function FAQSectionEditor({
  data,
  onUpdateData,
}: FAQSectionEditorProps) {
  const d = getData(data);

  const updateItem = (
    index: number,
    field: 'question' | 'answer',
    value: string,
  ) => {
    const next = [...(d.items ?? [])];
    if (!next[index]) next[index] = { question: '', answer: '' };
    next[index] = { ...next[index], [field]: value };
    onUpdateData({ ...data, items: next });
  };

  const addItem = () => {
    onUpdateData({
      ...data,
      items: [...(d.items ?? []), { question: '', answer: '' }],
    });
  };

  const removeItem = (index: number) => {
    const next = (d.items ?? []).filter((_, i) => i !== index);
    onUpdateData({ ...data, items: next });
  };

  return (
    <>
      <input
        type="text"
        className={styles.titleInput}
        value={d.title}
        onChange={(e) => onUpdateData({ ...data, title: e.target.value })}
        placeholder="Section title"
      />
      <div className={styles.list}>
        {(d.items ?? []).map((item, i) => (
          <div key={i} className={styles.item}>
            <input
              type="text"
              className={styles.qInput}
              value={item.question}
              onChange={(e) => updateItem(i, 'question', e.target.value)}
              placeholder="Question"
            />
            <textarea
              className={styles.aInput}
              value={item.answer}
              onChange={(e) => updateItem(i, 'answer', e.target.value)}
              placeholder="Answer"
              rows={2}
            />
            <button
              type="button"
              className={styles.removeBtn}
              onClick={() => removeItem(i)}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      <button type="button" className={styles.addBtn} onClick={addItem}>
        + Add question
      </button>
    </>
  );
}

interface FAQSectionProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function FAQSection({ data }: FAQSectionProps) {
  const d = getData(data);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.title}>{d.title}</h2>
        <div className={styles.list}>
          {(d.items ?? []).map((item, i) => (
            <div key={i} className={styles.item}>
              <button
                type="button"
                className={styles.accordionHead}
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                aria-expanded={openIndex === i}
              >
                {item.question}
                <span className={styles.chevron}>
                  {openIndex === i ? '−' : '+'}
                </span>
              </button>
              {openIndex === i && (
                <div className={styles.accordionBody}>{item.answer}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default FAQSection;
